import { ComponentType } from "@angular/cdk/portal";
import { Injectable, OnDestroy } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { MatDialog, MatDialogConfig, MatDialogRef } from "@angular/material/dialog";
import { EneLayoutService } from "src/app/core/services/layout.service";
import { Store } from "@ngrx/store";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { NotificationSettingsComponent } from "src/app/common/components/dialogs/notification-settings/components/notification-settings/notification-settings.component";
import { CampusOneAdminViewComponent } from "src/app/components/dialogs/campus-one-admin-view/campus-one-admin-view.component";
import { ContactDialogComponent } from "src/app/components/dialogs/contact-dialog/contact-dialog.component";
import { ContactDialogData } from "src/app/components/dialogs/contact-dialog/contact-dialog-data.interface";
import { GlossaryDialogComponent } from "src/app/components/dialogs/glossary-dialog/glossary-dialog.component";
import { OrderModuleDialogComponent } from "src/app/components/dialogs/order-module-dialog/container/order-module-dialog.component";
import {
  PrivacyAndTermsDialogComponent,
  PrivacyAndTermsTab
} from "src/app/components/dialogs/privacy-and-terms/privacy-and-terms.component";
import { ProductManagementDialogComponent } from "src/app/components/dialogs/product-management-dialog/container/product-management-dialog.component";
import { SimpleTextDialogComponent } from "src/app/components/dialogs/simple-text-dialog/simple-text-dialog.component";
import { RootState } from "src/app/ngrx/root-reducers";
import { selectCurrentPolicy } from "src/app/ngrx/selectors/app.selectors";
import { selectLaunchpadType } from "src/app/ngrx/selectors/overlay.selectors";
import { DeleteDialogComponent } from "src/app/shared/ui/delete-dialog/delete-dialog.component";
import { OrderFormEntry } from "../interfaces/contentful";
import { AuthService } from "./auth.service";

@Injectable({ providedIn: "root" })
export class DialogsService implements OnDestroy {
  private readonly destroy$ = new Subject<void>();

  private readonly currentPolicy = toSignal(this.store.select(selectCurrentPolicy));
  private readonly launchpadType = toSignal(this.store.select(selectLaunchpadType));

  constructor(
    private readonly layoutService: EneLayoutService,
    private readonly matDialog: MatDialog,
    private readonly store: Store<RootState>,
    private readonly authService: AuthService
  ) {}

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public open(component: ComponentType<unknown>, config: MatDialogConfig = {}): MatDialogRef<unknown, any> {
    const dialogRef = this.matDialog.open(component, config);

    this.layoutService.isMobileScreen$.pipe(takeUntil(this.destroy$)).subscribe((isMobileView) => {
      if (isMobileView) {
        dialogRef.updateSize("100vw", "100vh");
      } else {
        dialogRef.updateSize(config.width, config.height);
      }
    });

    return dialogRef;
  }

  public openPolicyDialog(tab?: PrivacyAndTermsTab): void {
    const isFirstVisit = !this.authService.hasPolicy(this.currentPolicy()?.version);

    this.open(PrivacyAndTermsDialogComponent, {
      panelClass: ["privacy-and-terms-dialog", "rounded"],
      width: "742px",
      autoFocus: false,
      data: { tab, isFirstVisit }
    });
  }

  public openGlossary(tab?: string): void {
    this.open(GlossaryDialogComponent, {
      panelClass: ["glossary-dialog", "rounded"],
      width: "742px",
      data: { type: this.launchpadType(), tab },
      autoFocus: false
    });
  }

  public openCampusOneAdminViewDialog(): void {
    this.open(CampusOneAdminViewComponent, {
      panelClass: "campus-one-admin-view-dialog",
      width: "100vw",
      height: "100vh",
      autoFocus: false
    });
  }

  public openOrderModuleDialog(moduleId: string, orderForm: OrderFormEntry): MatDialogRef<unknown, any> {
    return this.open(OrderModuleDialogComponent, {
      data: { moduleId, orderForm },
      panelClass: "order-module-dialog",
      width: "1236px",
      autoFocus: false
    });
  }

  public openProductManagementDialog(): void {
    this.open(ProductManagementDialogComponent, {
      panelClass: "product-management-dialog",
      width: "100vw",
      height: "100vh",
      autoFocus: false
    });
  }

  public openDeleteDialog(content: string, params?: Record<string, unknown>): MatDialogRef<unknown, boolean> {
    return this.open(DeleteDialogComponent, {
      panelClass: "delete-dialog",
      width: "480px",
      autoFocus: false,
      disableClose: true,
      data: { content, params }
    });
  }

  public openImprintDialog(lastPublish: string): void {
    this.open(SimpleTextDialogComponent, {
      autoFocus: false,
      panelClass: ["imprint-dialog", "rounded"],
      width: "742px",
      data: {
        heading: "IMPRINT_DIALOG.TITLE",
        content: "IMPRINT_DIALOG.CONTENT",
        params: { lastPublish }
      }
    });
  }

  public openContactDialog(data?: ContactDialogData): void {
    this.open(ContactDialogComponent, {
      panelClass: ["contact-dialog", "rounded"],
      autoFocus: false,
      width: "742px",
      data
    });
  }

  public openNotificationSettingsDialog(tab?: string): void {
    this.open(NotificationSettingsComponent, {
      panelClass: ["notification-settings-dialog"],
      autoFocus: false,
      width: "600px",
      data: { tab }
    });
  }
}
